import { useEffect, useMemo, useState } from "react";
import { Loader2, Send } from "lucide-react";

import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { renderLatexInline } from "@/components/RichMessageRenderer";

type StudentResponseCardProps = {
  question: string;
  disabled?: boolean;
  onSubmit: (answer: string) => Promise<void> | void;
};

type ChoiceOption = {
  key: string;
  text: string;
};

const stripMarkdown = (value: string) =>
  value
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .replace(/`/g, "")
    .trim();

const parseChoiceQuestion = (question: string) => {
  const lines = question
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const options: ChoiceOption[] = [];
  let firstOptionIndex = -1;

  lines.forEach((line, index) => {
    const match = stripMarkdown(line).match(/^(?:[-*]\s*)?(?:\[[ xX]\]\s*)?([A-D])\s*[).:-]\s*(.+)$/i);
    if (!match) return;

    if (firstOptionIndex === -1) firstOptionIndex = index;
    options.push({ key: match[1].toUpperCase(), text: match[2].trim() });
  });

  if (options.length < 2) return null;

  return {
    prompt: lines.slice(0, firstOptionIndex).join("\n").trim(),
    options,
  };
};

export function StudentResponseCard({ question, disabled, onSubmit }: StudentResponseCardProps) {
  const [draft, setDraft] = useState("");
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parsedChoiceQuestion = useMemo(() => parseChoiceQuestion(question), [question]);

  useEffect(() => {
    setDraft("");
    setSelectedOption(null);
    setIsSubmitting(false);
  }, [question]);

  const canSubmit = parsedChoiceQuestion ? !!selectedOption : !!draft.trim();

  const handleSubmit = async () => {
    if (!canSubmit || disabled || isSubmitting) return;

    const answer = parsedChoiceQuestion
      ? `${selectedOption} - ${parsedChoiceQuestion.options.find((option) => option.key === selectedOption)?.text ?? ""}`
      : draft.trim();

    if (!answer.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(answer);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-primary">Ta réponse</p>
        <p className="text-[11px] font-semibold text-muted-foreground">Vérifiée automatiquement</p>
      </div>

      {parsedChoiceQuestion ? (
        <div className="space-y-2">
          {parsedChoiceQuestion.prompt && (
            <p className="text-sm font-medium text-foreground">{renderLatexInline(parsedChoiceQuestion.prompt)}</p>
          )}

          <div className="grid gap-2">
            {parsedChoiceQuestion.options.map((option) => {
              const active = selectedOption === option.key;

              return (
                <button
                  key={option.key}
                  type="button"
                  disabled={disabled || isSubmitting}
                  onClick={() => setSelectedOption(option.key)}
                  className={cn(
                    "w-full rounded-2xl border px-3 py-3 text-left transition-all disabled:cursor-not-allowed disabled:opacity-60",
                    active
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border bg-background text-foreground hover:border-primary/30 hover:bg-muted/60",
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-muted text-sm font-black text-foreground">
                      {option.key}
                    </span>
                    <span className="pt-1 text-sm font-medium leading-relaxed">{renderLatexInline(option.text)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={disabled || isSubmitting}
          placeholder="Écris ta réponse ici..."
          className="min-h-[104px] resize-none border-border bg-background text-sm text-foreground placeholder:text-muted-foreground"
        />
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || disabled || isSubmitting}
          className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground transition-opacity disabled:opacity-50"
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Valider ma réponse
        </button>
      </div>
    </div>
  );
}

export default StudentResponseCard;